import React, { useCallback } from 'react';
import { OnUploadStartCallback, UploadsButton } from '@design-stack-vista/upload-components';
import { Box, Card, FlexBox, FluidImage, Icon, Typography } from '@vp/swan';
import { AnalyticsEventData } from 'src/components/Analytics';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { Upload } from 'src/services/UploadAndPrintService';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { useFileUpload } from './useFileUpload';

interface UploadPropertiesProps {
  upload: Upload;
  productInformation: ProductInformation;
  onUploadStart: OnUploadStartCallback;
}

export const UploadProperties = ({ upload, productInformation, onUploadStart }: UploadPropertiesProps) => {
  const { resetUploader, handleBeforeUpload } = useFileUpload();
  const { sendSegmentTrackingEvent } = useTrackingEvent();

  const onClickReplaceUploadButton: OnUploadStartCallback = useCallback(
    results => {
      onUploadStart(results);
      sendSegmentTrackingEvent({
        action: UPLOAD_UNIFIED_EVENT,
        category: 'Design - Upload and Print',
        uploadStep: UploadSteps.FileReplaced,
        label: 'Replace',
      } as AnalyticsEventData);
    },
    [onUploadStart, sendSegmentTrackingEvent]
  );
  const onClickDeleteButton = useCallback(() => {
    resetUploader();
    sendSegmentTrackingEvent({
      action: UPLOAD_UNIFIED_EVENT,
      category: 'Design - Upload and Print',
      uploadStep: UploadSteps.FileDeleted,
      label: 'Delete',
    } as AnalyticsEventData);
  }, [resetUploader, sendSegmentTrackingEvent]);

  return (
    <Card bordered padding={0}>
      <FlexBox className="upload-properties-container" gap={5}>
        <FlexBox
          className="thumbnail-container"
          padding={5}
          backgroundColor="strong"
          justifyContent="center"
          alignItems="center"
        >
          <FluidImage className="thumbnail" src={upload.thumbnailUrl} alt={upload.uploadedFileName} />
        </FlexBox>
        <FlexBox className="upload-detail" flexDirection="column" paddingY="6" paddingRight="5">
          <FlexBox alignItems="center" marginBottom="6">
            <Box className="upload-detail-name">
              <Typography fontSkin="body-standard-bold" truncate>
                {upload.uploadedFileName}
              </Typography>
              <Typography fontSize="small">{productInformation.selectedOptions['Size']}</Typography>
            </Box>
            <Icon
              className="delete-icon"
              onClick={onClickDeleteButton}
              display="inline-block"
              iconType="delete"
              size="24p"
              skin="standard"
              alt={text('remove-upload-title')}
            />
          </FlexBox>
          <UploadsButton
            onBeforeUpload={handleBeforeUpload}
            onUploadStart={onClickReplaceUploadButton}
            size="mini"
            width="full-width"
          >
            {text('replace-upload-label')}
          </UploadsButton>
        </FlexBox>
      </FlexBox>
    </Card>
  );
};
